"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslations } from "next-intl"
import { useFormatter } from "@/hooks/use-formatter"

import { ApolloPageShell, ApolloPageHeader } from "@/components/ui/apollo-page"
import { MyTasksWidget } from "@/components/dashboard/my-tasks-widget"
import { TargetProgressWidget } from "@/components/dashboard/target-progress-widget"
import { LeaderboardWidget } from "@/components/dashboard/leaderboard-widget"
import { DashboardStats } from "@/components/dashboard/dashboard-stats"
import { LeadsStatusChart } from "@/components/dashboard/leads-status-chart"
import { AppointmentsStatusChart } from "@/components/dashboard/appointments-status-chart"
import { QuotesFunnelChart } from "@/components/dashboard/quotes-funnel-chart"
import { useDashboardWidgetFlags } from "@/components/dashboard/use-dashboard-widget-flags"
import { usePermissions } from "@/hooks/use-permissions"
import { cn } from "@/lib/utils"

import { getLeadStats } from "@/lib/leads-api"
import { getAppointmentStats } from "@/lib/appointments-api"
import { getQuoteStats } from "@/lib/quotes-api"
import { getMyProgress, refreshMyProgress, getLeaderboard } from "@/lib/targets-api"
import type { LeadStats, AppointmentStats, QuoteStats } from "@/types"
import type { MyProgressResponse, LeaderboardResponse } from "@/lib/targets-api"

const LEAD_VIEW_PERMISSIONS = ["leads.view.assigned", "leads.view.all"]

/** Column count → static Tailwind class. Never interpolate into a class name. */
const CHART_GRID_COLS: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-1 md:grid-cols-2",
  3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
}

interface DashboardData {
  leadStats: LeadStats | null
  appointmentStats: AppointmentStats | null
  quoteStats: QuoteStats | null
  myProgress: MyProgressResponse | null
  leaderboard: LeaderboardResponse | null
}

const EMPTY_DATA: DashboardData = {
  leadStats: null,
  appointmentStats: null,
  quoteStats: null,
  myProgress: null,
  leaderboard: null,
}

/**
 * Generic (role-agnostic) dashboard — the fallback variant rendered for
 * lab_technician and any per-clinic custom role that does not map onto one of
 * the six specialist dashboards.
 *
 * Content is the pre-split dashboard body verbatim (stats strip, monthly
 * target + leaderboard row, three charts) with ONE behavioural change: every
 * fetch is permission-gated, and a widget whose data was never fetched is
 * OMITTED rather than rendered empty (hide-not-disable). Gating waits for
 * `usePermissions().isLoaded` so nothing fires-and-discards during auth
 * hydration.
 *
 * The doctor welcome band that used to live here has moved to
 * `doctor-dashboard.tsx`.
 */
export function GenericDashboard() {
  const t = useTranslations("dashboard")
  const formatter = useFormatter()
  const { isLoaded, can, canAny } = usePermissions()
  const { showLeadsStatusChart, showQuoteFunnel } = useDashboardWidgetFlags()

  const [data, setData] = useState<DashboardData>(EMPTY_DATA)
  const [isLoading, setIsLoading] = useState(true)

  const canViewLeads = canAny(LEAD_VIEW_PERMISSIONS)
  const canViewAppointments = can("appointments.view")
  const canViewQuotes = can("quotes.view")
  const canViewTargets = can("targets.view")

  useEffect(() => {
    // Hold every request until the permission set has resolved — firing first
    // and discarding would 403 for users who legitimately lack the grant.
    if (!isLoaded) return

    let cancelled = false
    const loadDashboard = async () => {
      setIsLoading(true)
      try {
        const [leadStats, appointmentStats, quoteStats, myProgress, leaderboard] =
          await Promise.all([
            canViewLeads ? getLeadStats().catch(() => null) : Promise.resolve(null),
            canViewAppointments
              ? getAppointmentStats().catch(() => null)
              : Promise.resolve(null),
            canViewQuotes ? getQuoteStats().catch(() => null) : Promise.resolve(null),
            canViewTargets ? getMyProgress().catch(() => null) : Promise.resolve(null),
            canViewTargets ? getLeaderboard().catch(() => null) : Promise.resolve(null),
          ])
        if (!cancelled) {
          setData({ leadStats, appointmentStats, quoteStats, myProgress, leaderboard })
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    loadDashboard()

    return () => {
      cancelled = true
    }
  }, [isLoaded, canViewLeads, canViewAppointments, canViewQuotes, canViewTargets])

  const handleRefreshProgress = useCallback(async () => {
    await refreshMyProgress()
    const newProgress = await getMyProgress()
    setData((prev) => ({ ...prev, myProgress: newProgress }))
  }, [])

  // Localized month-year for the header eyebrow (no hardcoded month names).
  const monthYear = formatter.dateTime(new Date(), {
    month: "long",
    year: "numeric",
  })

  // The stats strip is a single widget fed by three sources; render it as long
  // as at least one source was fetched, and leave the ungranted tiles at their
  // null-safe zero. Drop it entirely when the user can see none of the three.
  const showStats = canViewLeads || canViewAppointments || canViewQuotes

  const charts = useMemo(() => {
    const nodes: React.ReactNode[] = []
    if (canViewLeads && showLeadsStatusChart) {
      nodes.push(
        <LeadsStatusChart key="leads" data={data.leadStats} isLoading={isLoading} />,
      )
    }
    if (canViewAppointments) {
      nodes.push(
        <AppointmentsStatusChart
          key="appointments"
          data={data.appointmentStats}
          isLoading={isLoading}
        />,
      )
    }
    if (canViewQuotes && showQuoteFunnel) {
      nodes.push(
        <QuotesFunnelChart key="quotes" data={data.quoteStats} isLoading={isLoading} />,
      )
    }
    return nodes
  }, [
    canViewLeads,
    canViewAppointments,
    canViewQuotes,
    showLeadsStatusChart,
    showQuoteFunnel,
    data,
    isLoading,
  ])

  return (
    <ApolloPageShell>
      {/* Header band */}
      <ApolloPageHeader
        eyebrow={`${t("page.eyebrow")} · ${monthYear}`}
        title={t("page.header")}
        stats={
          showStats ? (
            <DashboardStats
              leadStats={data.leadStats}
              appointmentStats={data.appointmentStats}
              quoteStats={data.quoteStats}
              isLoading={isLoading}
            />
          ) : undefined
        }
      />

      {/* Scrollable content area */}
      <div className="scrollbar-thin min-h-0 flex-1 overflow-auto">
        <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
          {/* My Tasks — self-gating (tasks_enabled + tasks.view.assigned).
              This variant is what simple-mode clinics render, so tasks MUST
              surface here. */}
          <MyTasksWidget />

          {/* Row: Target (4fr) + Leaderboard (2fr) — both come from targets.view */}
          {canViewTargets && (
            <div className="grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-6">
              <div className="lg:col-span-4">
                <TargetProgressWidget
                  data={data.myProgress}
                  isLoading={isLoading}
                  onRefresh={handleRefreshProgress}
                />
              </div>
              <div className="lg:col-span-2">
                <LeaderboardWidget data={data.leaderboard} isLoading={isLoading} />
              </div>
            </div>
          )}

          {/* Row: Charts — the grid collapses to however many survived gating */}
          {charts.length > 0 && (
            <div
              className={cn(
                "grid gap-4 md:gap-6",
                CHART_GRID_COLS[charts.length] ?? CHART_GRID_COLS[3],
              )}
            >
              {charts}
            </div>
          )}
        </div>
      </div>
    </ApolloPageShell>
  )
}
